import React from 'react';
import { motion } from 'framer-motion';
import './Services.css';

interface Service {
  id: number;
  icon: string;
  title: string;
  description: string;
  features: string[];
  price: string;
  popular?: boolean;
}

const Services: React.FC = () => {
  const services: Service[] = [
    {
      id: 1,
      icon: "📱",
      title: "Desarrollo de Apps Móviles",
      description: "Aplicaciones nativas y multiplataforma con las últimas tecnologías y estándares de seguridad.",
      features: [
        "Apps nativas iOS y Android",
        "Desarrollo Flutter/React Native",
        "Integración con APIs",
        "Cifrado de extremo a extremo",
        "UI/UX moderno y intuitivo",
        "Testing automático"
      ],
      price: "Desde 1.000€"
    },
    {
      id: 2,
      icon: "💻",
      title: "Desarrollo Web Full-Stack",
      description: "Aplicaciones web escalables con arquitectura moderna, diseño responsive y optimización SEO.",
      features: [
        "Frontend React/Vue.js",
        "Backend Node.js/Python",
        "Bases de datos optimizadas",
        "Diseño responsive",
        "SEO optimizado",
        "Panel de administración"
      ],
      price: "Desde 500€",
      popular: true
    },
    {
      id: 3,
      icon: "🛡️",
      title: "Auditoría de Seguridad",
      description: "Análisis exhaustivo de vulnerabilidades y fortalecimiento de la seguridad de tus sistemas.",
      features: [
        "Pentesting completo",
        "Análisis de vulnerabilidades",
        "Auditoría de código",
        "Informe detallado",
        "Plan de mitigación",
        "Seguimiento post-auditoría"
      ],
      price: "Desde 500€"
    },
    {
      id: 4,
      icon: "🤖",
      title: "Soluciones de IA/ML",
      description: "Implementación de inteligencia artificial y machine learning para automatizar procesos.",
      features: [
        "Modelos de ML personalizados",
        "Procesamiento de lenguaje natural",
        "Visión por computadora",
        "Análisis predictivo",
        "Chatbots inteligentes",
        "Automatización de procesos"
      ],
      price: "Desde 1.000€"
    },
    {
      id: 5,
      icon: "🔐",
      title: "Consultoría en Privacidad",
      description: "Asesoramiento especializado en protección de datos y cumplimiento normativo GDPR.",
      features: [
        "Auditoría GDPR/LOPD",
        "Políticas de privacidad",
        "Evaluación de impacto",
        "Formación del equipo",
        "Procedimientos DPO",
        "Monitoreo continuo"
      ],
      price: "Desde 500€"
    },
    {
      id: 6,
      icon: "⚡",
      title: "Optimización y Mantenimiento",
      description: "Mejora del rendimiento de aplicaciones existentes y mantenimiento proactivo.",
      features: [
        "Optimización de rendimiento",
        "Refactorización de código",
        "Actualizaciones de seguridad",
        "Monitoreo 24/7",
        "Backup automático",
        "Soporte técnico"
      ],
      price: "Desde 500€"
    },
    {
      id: 7,
      icon: "⚙️",
      title: "Automatización de Tareas",
      description: "Eliminamos tareas repetitivas en tu negocio con scripts, RPA y workflows inteligentes que liberan a tu equipo.",
      features: [
        "Scripts a medida (Python, Node.js)",
        "Workflows con n8n / Zapier / Make",
        "Integración entre apps y APIs",
        "Procesamiento masivo de datos",
        "Automatización de email y reportes",
        "Bots para Telegram, WhatsApp y Slack"
      ],
      price: "Desde 400€"
    },
    {
      id: 8,
      icon: "🧠",
      title: "Agentes de IA y LLMs",
      description: "Integramos modelos de lenguaje (GPT, Claude, Llama) en tus procesos: chatbots, asistentes internos, clasificación y análisis de documentos.",
      features: [
        "Chatbots y asistentes con RAG",
        "Agentes autónomos multi-paso",
        "Fine-tuning y modelos open-source",
        "Análisis y resumen de documentos",
        "Integración con tu base de conocimiento",
        "Modelos privados on-premise (sin fugas)"
      ],
      price: "Desde 1.500€"
    },
    {
      id: 9,
      icon: "📈",
      title: "Marketing Digital y Campañas",
      description: "Atraemos clientes a tu negocio con campañas de Google Ads, Meta Ads, SEO técnico y analítica avanzada.",
      features: [
        "Google Ads (Search, Display, YouTube)",
        "Meta Ads (Facebook & Instagram)",
        "SEO técnico y on-page",
        "Analítica con GA4 y Search Console",
        "Landing pages de alta conversión",
        "Email marketing y automatizaciones"
      ],
      price: "Desde 600€/mes"
    },
    {
      id: 10,
      icon: "🚀",
      title: "Digitalización de Negocios",
      description: "Acompañamos a empresas, comercios y startups en todo el proceso de transformación digital, desde el diagnóstico hasta el despliegue.",
      features: [
        "Diagnóstico digital y consultoría",
        "Diseño de arquitectura tecnológica",
        "Migración a la nube (AWS, GCP, Azure)",
        "Integración de ERPs y CRMs",
        "Formación interna del equipo",
        "Acompañamiento continuo post-despliegue"
      ],
      price: "Presupuesto a medida"
    },
    {
      id: 11,
      icon: "🔬",
      title: "Reverse Engineering",
      description: "Análisis avanzado de binarios, apps y firmware. Auditoría de seguridad de bajo nivel, ingeniería inversa de protocolos y reparación de software.",
      features: [
        "Análisis estático y dinámico (Ghidra, IDA)",
        "Reversing de apps Android e iOS",
        "Auditoría de firmware y dispositivos IoT",
        "Análisis de protocolos propietarios",
        "Detección de malware y backdoors",
        "Informes técnicos forenses"
      ],
      price: "Desde 800€"
    },
    {
      id: 12,
      icon: "🔌",
      title: "Integraciones y APIs",
      description: "Conectamos tus sistemas: APIs REST y GraphQL, webhooks, microservicios y conectores entre ERP, CRM, e-commerce y pasarelas de pago.",
      features: [
        "APIs REST y GraphQL a medida",
        "Integraciones ERP, CRM y e-commerce",
        "Pasarelas de pago (Stripe, Redsys, PayPal)",
        "Webhooks y eventos en tiempo real",
        "Arquitectura de microservicios",
        "Documentación OpenAPI / Swagger"
      ],
      price: "Desde 500€"
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <section id="services" className="services section">
      <div className="container">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="section-title neon-text animate-glow"
            variants={fadeInUp}
          >
            Servicios Profesionales
          </motion.h2>

          <motion.p 
            className="services-subtitle"
            variants={fadeInUp}
          >
            Ofrecemos soluciones tecnológicas integrales con el más alto nivel de seguridad 
            y calidad. Cada servicio está diseñado para impulsar tu negocio hacia el futuro digital.
          </motion.p>

          <motion.div 
            className="services-grid"
            variants={staggerContainer}
          >
            {services.map((service) => (
              <motion.div
                key={service.id}
                className={`service-card glass-card ${service.popular ? 'popular' : ''}`}
                variants={fadeInUp}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                {service.popular && (
                  <div className="popular-badge">
                    <span>⭐ Más Popular</span>
                  </div>
                )}

                <div className="service-header">
                  <div className="service-icon">{service.icon}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>

                <div className="service-features">
                  <h4>Incluye:</h4>
                  <ul>
                    {service.features.map((feature, index) => (
                      <li key={index}>
                        <span className="feature-check">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-footer">
                  <div className="service-price">{service.price}</div>
                  <button 
                    className="btn-secondary service-btn"
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Solicitar Presupuesto
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="services-cta"
            variants={fadeInUp}
          >
            <div className="cta-content">
              <h3 className="gradient-text">¿Necesitas algo personalizado?</h3>
              <p>
                Cada negocio es único. Creamos soluciones completamente personalizadas 
                que se adaptan perfectamente a tus necesidades específicas.
              </p>
              <div className="cta-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Consulta Gratuita
                </button>
                <a 
                  href="https://www.fiverr.com/sirhoff/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  Ver en Fiverr
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="process-section"
            variants={fadeInUp}
          >
            <h3 className="neon-text">Nuestro Proceso de Trabajo</h3>
            <div className="process-grid">
              <div className="process-step">
                <div className="step-number">01</div>
                <div className="step-content">
                  <h4>Análisis</h4>
                  <p>Entendemos tus necesidades y objetivos específicos</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="step-number">02</div>
                <div className="step-content">
                  <h4>Planificación</h4>
                  <p>Diseñamos la arquitectura y estrategia óptima</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="step-number">03</div>
                <div className="step-content">
                  <h4>Desarrollo</h4>
                  <p>Implementamos con código limpio y seguro</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="step-number">04</div>
                <div className="step-content">
                  <h4>Entrega</h4>
                  <p>Testing completo y despliegue profesional</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;